import {
    getCustomerFlow,
    getFlowType,
    getLoginData,
    getSiteStructure,
    MAP_SALESCHANNEL_TO_TARGET_AUDIENCE,
    PageViewTrackingData,
    PageViewTrackingDataWithError,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_SITE_AREA_MOBILITY,
    TrackingBtx,
    TrackingError,
    TrackingFacet,
    TrackingFilterType,
    TrackingLoginStatus,
    TrackingPageType,
    TrackingProduct,
    TrackingProductType,
    TrackingProposition,
    TrackingTargetAudience,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';
import {
    BTX_BNT,
    FLOW_SUBSCRIPTIONS_FIRST,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_YOUNG,
} from '@vfde-sails/constants';
import { TRACKING_PAGENAME } from '../constants';
import {
    AbstractedUpsellCategory,
    AbstractedUpsellItem,
    isUpsellItem,
    PriceType,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import { SelectorReturnType } from '@vfde-sails/types';
import { selectTrackingPayload } from '../selectors';
import { SailsBntStorage } from '@vfde-sails/storage';

/**
 * Create the pageview tracking object
 */
export const createPageViewTrackingPayload = (
    trackingData: SelectorReturnType<typeof selectTrackingPayload>,
    storage: SailsBntStorage | null,
): PageViewTrackingData => {
    const {
        atomicName,
        devicePrice,
        tariffName,
        subscriptionId,
        tariffRuntime,
        tariffMonthlyPrice,
        salesChannel,
        selectedCategories,
        selectedProducts,
        selectedDevice,
    } = trackingData;
    const siteStructure = getSiteStructure(TRACKING_PAGENAME, BTX_BNT);
    const loginStatus = getLoginData() ? TrackingLoginStatus.LoggedIn : TrackingLoginStatus.NotLoggedIn;
    const lineOfCustomer = getCustomerFlow(salesChannel || undefined);

    const trackingPayload: PageViewTrackingData = {
        /* eslint-disable @typescript-eslint/naming-convention, camelcase */
        page_levels: siteStructure,
        page_type: TrackingPageType.ProductListing,
        login_status: loginStatus,
        site_area: TRACKING_SITE_AREA_MOBILITY,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        flow_type: getFlowType(storage?.flowType || FLOW_SUBSCRIPTIONS_FIRST),
        business_transaction_type: TrackingBtx.Bnt,
        line_of_customer: lineOfCustomer,
        appliedFilters: selectedCategories.map(category => ({
            facet: TrackingFacet.Characteristic,
            value: category.value,
        })),
        /* eslint-enable @typescript-eslint/naming-convention, camelcase */
    };

    if (atomicName || tariffName) {
        let productCategory: TrackingProposition['productCategory'];

        if (atomicName && tariffName) {
            productCategory = TrackingProductType.Bundle;
        }
        else if (atomicName) {
            productCategory = TrackingProductType.Device;
        }
        else {
            productCategory = TrackingProductType.Tariff;
        }

        const propositions: TrackingProposition[] = [
            {
                id: 1,
                name: `${atomicName || ''}${atomicName && tariffName ? ' | ' : ''}${tariffName || ''}`,
                retention: TrackingBtx.Bnt,
                productType: TRACKING_PRODUCT_TYPE,
                productLine: TRACKING_PRODUCT_LINE,
                productCategory,
                targetAudience: (
                    MAP_SALESCHANNEL_TO_TARGET_AUDIENCE[salesChannel as typeof SALESCHANNEL_CONSUMER | typeof SALESCHANNEL_YOUNG]
                    || TrackingTargetAudience.Consumer
                ),
                units: 1,
            },
        ];

        const products = [];

        if (atomicName) {
            const trackingDevice: TrackingProduct = {
                type: TrackingProductType.Device,
                name: atomicName,
                sku: selectedDevice!.sku,
                priceOnce: `${devicePrice! }`,
                priceMonthly: '0',
                proposition: 1,
                units: 1,
            };

            products.push(trackingDevice);
        }

        if (tariffName) {
            const trackingTariff: TrackingProduct = {
                type: TrackingProductType.Tariff,
                name: tariffName,
                sku: subscriptionId!,
                duration: tariffRuntime,
                priceMonthly: `${tariffMonthlyPrice!}`,
                priceOnce: '0',
                proposition: 1,
                units: 1,
            };

            products.push(trackingTariff);
        }

        products.push(...selectedProducts
            ?.filter(selectedProduct => isUpsellItem(selectedProduct))
            .map(selectedProduct => convertProductToTrackingProduct(selectedProduct as AbstractedUpsellItem)) || [],
        );

        trackingPayload.oOrder = {
            aProducts: products,
            aPropositions: propositions,
        };
    }

    return trackingPayload;
};

/**
 * Converts a Product object to a TrackingProduct object
 * @param product The product to convert
 */
export const convertProductToTrackingProduct = (product: AbstractedUpsellItem): TrackingProduct => {
    const { id, label, price: prices } = product;
    const trackingProduct: TrackingProduct = {
        sku: id,
        name: label,
        type: getTrackingProductType(product),
        proposition: 1,
        units: 1,
    };

    for (const price of prices) {
        const priceValue = price.value;

        if (price.type === PriceType.Onetime) {
            trackingProduct.priceOnce = priceValue;
        }
        else {
            trackingProduct.priceMonthly = priceValue;
        }
    }

    return trackingProduct;
};

/**
 * Returns the product tracking type for the given product
 * based on the products category
 * @param product The product
 * @returns Tracking Product Type
 */
export const getTrackingProductType = (product: AbstractedUpsellItem): TrackingProduct['type'] => {
    // Hardcoding to 0 aligned with Zuse, fine for the upsell service
    switch (product.category.value) {
        case UpsellFacetValue.Redplus:
            return TrackingProductType.Tariff;
        case UpsellFacetValue.Insurance:
            return TrackingProductType.Service;
        default:
            return TrackingProductType.Accessory;
    }
};

/**
 * Tracks when a category filter is added or removed
 * @param wasAdded - When the category was enabled true, false when the filter was disabled
 * @param updatedCategory - The added or removed category object
 * @param selectedCategories - The selected categories
 */
export const trackToggleCategory = (
    wasAdded: boolean,
    updatedCategory: AbstractedUpsellCategory,
    selectedCategories: AbstractedUpsellCategory[],
) => {
    trackIt(TrackType.FilterProducts, {
        action: wasAdded ? TrackingFilterType.Add : TrackingFilterType.Remove,
        updatedFilter: [
            {
                facet: TrackingFacet.Characteristic,
                value: updatedCategory.value,
            },
        ],
        appliedFilters: selectedCategories.map(selectedCatgory => ({
            facet: TrackingFacet.Characteristic,
            value: selectedCatgory.value,
        })),
    });
};

/**
 * Track when a product was selected or deselected
 */
export const trackToggleProduct = (
    selectedProducts: AbstractedUpsellItem[],
    removedProducts: AbstractedUpsellItem[],
) => {
    const trackingData: Record<string, TrackingProduct[]> = {};

    if (selectedProducts.length) {
        trackingData.selectedOption = selectedProducts.map(product => convertProductToTrackingProduct(product));
    }

    if (removedProducts.length) {
        trackingData.removedOption = removedProducts.map(product => convertProductToTrackingProduct(product));
    }

    trackIt(TrackType.SwitchOptions, trackingData);
};

/**
 * Tracks errors
 */
export const trackError = (trackingError: TrackingError) => {
    trackIt(TrackType.Error, trackingError);
};

/**
 * Create the page view tracking object with error
 */
export const createPageViewTrackingDataWithError = (
    trackingData: Parameters<typeof createPageViewTrackingPayload>[0],
    storage: Parameters<typeof createPageViewTrackingPayload>[1],
    trackingError: TrackingError,
): PageViewTrackingDataWithError => {
    const pageViewTrackingData = createPageViewTrackingPayload(trackingData, storage);

    return {
        ...pageViewTrackingData,
        ...trackingError,
    };
};
